import {useCallback, useState} from "react";
import {useIntl} from "react-intl";
import {useNumberListQuery} from "./queries.generated";
import {Autocomplete, TextField} from "@mui/material";
import {ClearIcon} from "@mui/x-date-pickers/icons";

/** This seems to be the best width for the component. */
const MIN_WIDTH = 300;

type Props = {
    numberId: number;
    setNumberId: (v: number) => void;
    domainId: number;
    disabled: boolean;
};

type Option = {
    value: number;
    label: string;
};

type DataSource = {
    state: "initial" | "loading" | "loaded";
    options: Option[];
    load: () => void;
};

function useDataSource(domainId: number): DataSource {
    const [loadQuery, setLoadQuery] = useState<boolean>(false);
    const query = useNumberListQuery({
        variables: {domainId},
        fetchPolicy: "no-cache",
        skip: !loadQuery,
    });

    const load = useCallback(() => {
        if (!loadQuery) setLoadQuery(true);
    }, [loadQuery]);

    if (!loadQuery)
        return {
            state: "initial",
            options: [],
            load,
        };

    if (query.loading)
        return {
            state: "loading",
            options: [],
            load,
        };

    const options: Option[] = query.data.users.items.map((user) => ({
        value: user.numberId,
        label: `${user.number.shortNumber} - ${user.displayName}`,
    }));

    options.push(
        ...query.data.groups.items
            .filter((group) => !!group.number.shortNumber)
            .map((group) => ({
                value: group.numberId,
                label: `${group.number.shortNumber} - ${group.name}`,
            })),
    );

    options.push(
        ...query.data.ivrs.items.map((ivr) => ({
            value: ivr.numberId,
            label: `${ivr.number.shortNumber} - ${ivr.name || ivr.number.shortNumber}`,
        })),
    );

    options.sort((a, b) => a.label.localeCompare(b.label));

    return {
        state: "loaded",
        options,
        load,
    };
}

export const NumberWidget = ({numberId, setNumberId, domainId, disabled}: Props) => {
    const intl = useIntl();
    const placeholder = intl.formatMessage({id: "call_list.filters.number.any", defaultMessage: "Any number"});
    const dataSource = useDataSource(domainId);

    return (
        <Autocomplete
            disabled={disabled}
            disablePortal
            options={dataSource.options}
            clearIcon={
                <span onClick={() => setNumberId(undefined)} style={{display: "flex"}}>
                    <ClearIcon fontSize="small" />
                </span>
            }
            getOptionLabel={(option: Option) => option.label}
            value={dataSource.options.find((op) => op.value === numberId) ?? null}
            onChange={(_, op) => {
                if (op) setNumberId(op.value);
            }}
            renderInput={(params) => (
                <TextField
                    placeholder={placeholder}
                    onClick={dataSource.load}
                    variant="outlined"
                    margin="dense"
                    style={{margin: 0}}
                    size="small"
                    {...params}
                />
            )}
            sx={{minWidth: MIN_WIDTH, width: "fit-content"}}
        />
    );
};
